import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    Output,
} from '@angular/core';
import he from 'he';
import { map, switchMap, take } from 'rxjs/operators';

import { environment } from '@web-env/environment';

import type { Uuid } from '@common/types';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';
import { ESelectedEntityType } from '@common/types/layout.type';
import { AsyncPipe } from '@angular/common';
import { SpEditorComponent } from '@libs/components/sp-editor/src/sp-editor.component';
import { SpSafeHtmlPipe } from '@common/pipes/safe-html.pipe';

@Component({
    selector: 'web-editor',
    templateUrl: './web-editor.component.html',
    styleUrls: ['./web-editor.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpEditorComponent, AsyncPipe, SpSafeHtmlPipe],
})
export class WebEditorComponent {
    @Input() public inline?: boolean = true;
    @Input() public content: string;
    @Input() public containerId: Uuid;
    @Input() public changeContentOnKeyUp: boolean = true;
    @Input() public toolbarMenu: string;
    @Input() public placeholder: string;
    @Input() public isPasteAsText: boolean;
    @Input() public widgetId: string;
    @Input() public useExperimentalFocus: boolean;
    @Input() public useExperimentalDblClickFocus: boolean;

    @Output() public changeContent = new EventEmitter<string>();
    @Output() public focusInEvent = new EventEmitter<void>();
    @Output() public focusOutEvent = new EventEmitter<string>();
    @Output() public isEmptyContent = new EventEmitter<boolean>();
    @Output() public changeEditorHeight = new EventEmitter<number>();

    private readonly themeState = inject(ThemeStateService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    private readonly sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    public readonly baseHref = environment.baseHref;
    public readonly selectedThemeColors = this.themeState.selectedColors;
    public readonly selectedDeviceMode$ = this.layoutFacade.deviceMode$;

    public isFocused = false;
    public showEditor = false;
    public showTextGradientModal = false;
    public showAiModal = false;

    public changeHandler(value: string) {
        const strippedVal = he.decode(value.replace(/(<([^>]+)>)/gi, '')).trim();

        const valHasImage = value.includes('<img');

        if (strippedVal.length === 0 && !valHasImage) {
            return this.changeContent.emit(strippedVal);
        }
        this.changeContent.emit(value);
    }

    public editWidgetHandler() {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.widget, entityId: this.widgetId });
    }

    public editWidgetInNavigationHandler() {
        // The block/section ids aren't on the widget entity, so resolve them
        // on-demand from the widget's columnId (column → section → block).
        this.widgetsFacade
            .widgetById$(this.widgetId)
            .pipe(
                take(1),
                switchMap((widget) =>
                    this.columnsFacade.columnById$(widget?.columnId).pipe(
                        take(1),
                        switchMap((column) =>
                            this.sectionsFacade.sectionById$(column?.parentId).pipe(
                                take(1),
                                map((section) => ({ widget, column, section }))
                            )
                        )
                    )
                )
            )
            .subscribe(({ widget, column, section }) => {
                this.navDrawerService.selectInNavigation({
                    entityId: this.widgetId,
                    blockId: section?.parentId,
                    sectionId: column?.parentId,
                    columnId: widget?.columnId,
                    gridId: widget?.structureId,
                });
            });
    }

    public focusInEventHandler() {
        this.isFocused = true;
        this.focusInEvent.emit();
    }

    public showTextGradientModalHandler(value) {
        this.showTextGradientModal = value;
    }

    public showAiModalHandler(value) {
        this.showAiModal = value;
    }

    public focusOutWithoutContentEventHandler() {
        if (!this.useExperimentalFocus) {
            return;
        }

        if (!this.showTextGradientModal && !this.showAiModal) {
            setTimeout(() => {
                this.isFocused = false;
                this.changeDetectorRef.detectChanges();
            }, 100);
        }
    }
}
